// Поисковая строка адреса: ?q=…&sort=колонка:asc&f.ключ=значение&tab=…&p=вид:id,вид:id
//
// Правило одной строки: одна и та же выборка всегда даёт ОДИН И ТОТ ЖЕ адрес. Поэтому
// порядок параметров фиксирован (q, sort, фильтры, tab, p), а ключи фильтров сортируются.
// Без этого две ссылки на одно и то же выглядят разными, и «открой у себя» превращается
// в сверку строк глазами.
//
// Чистые функции без React. Значения кодируются; структурные разделители «:» и «,»
// остаются голыми, поэтому вид панели и идентификатор не могут содержать их сами —
// у нас это слаги и UUID, ограничение ничего не отнимает.

export const MAX_PANELS = 3;

export type SortDir = 'asc' | 'desc';

export interface SortSpec {
  column: string;
  dir: SortDir;
}

/** Панель в стопке: вид (employee, claim, document…) и идентификатор сущности. */
export interface PanelRef {
  view: string;
  id: string;
}

export interface SearchState {
  q?: string;
  sort?: SortSpec;
  filters: Readonly<Record<string, string>>;
  tab?: string;
  /** Стопка панелей, максимум MAX_PANELS; последняя — верхняя. */
  panels: readonly PanelRef[];
}

export const EMPTY_SEARCH: SearchState = { filters: {}, panels: [] };

export function parseSearch(search: string): SearchState {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const filters: Record<string, string> = {};
  let q: string | undefined;
  let sort: SortSpec | undefined;
  let tab: string | undefined;
  let panels: PanelRef[] = [];

  for (const [name, value] of params) {
    if (name === 'q' && value !== '') {
      q = value;
    } else if (name === 'sort') {
      sort = parseSort(value) ?? sort;
    } else if (name === 'tab' && value !== '') {
      tab = value;
    } else if (name === 'p') {
      panels = parsePanels(value);
    } else if (name.startsWith('f.') && name.length > 2) {
      filters[name.slice(2)] = value;
    }
    // Незнакомые параметры молча пропускаются: чужие метки (например, отслеживание
    // источника ссылки) не должны ломать разбор состояния.
  }

  return { q, sort, filters, tab, panels };
}

function parseSort(value: string): SortSpec | undefined {
  const separator = value.lastIndexOf(':');
  if (separator <= 0) {
    return undefined;
  }
  const column = value.slice(0, separator);
  const dir = value.slice(separator + 1);
  return dir === 'asc' || dir === 'desc' ? { column, dir } : undefined;
}

function parsePanels(value: string): PanelRef[] {
  const panels: PanelRef[] = [];
  for (const chunk of value.split(',')) {
    const separator = chunk.indexOf(':');
    if (separator <= 0 || separator === chunk.length - 1) {
      continue; // битый кусок пропускаем, остальная стопка остаётся рабочей
    }
    panels.push({ view: chunk.slice(0, separator), id: chunk.slice(separator + 1) });
    if (panels.length === MAX_PANELS) {
      break; // потолок стопки держим и на разборе: руками дописанный адрес его не обходит
    }
  }
  return panels;
}

export function buildSearch(state: SearchState): string {
  const parts: string[] = [];
  if (state.q !== undefined && state.q !== '') {
    parts.push(`q=${encodeURIComponent(state.q)}`);
  }
  if (state.sort !== undefined) {
    parts.push(`sort=${encodeURIComponent(state.sort.column)}:${state.sort.dir}`);
  }
  for (const [key, value] of Object.entries(state.filters).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  )) {
    parts.push(`f.${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  if (state.tab !== undefined && state.tab !== '') {
    parts.push(`tab=${encodeURIComponent(state.tab)}`);
  }
  if (state.panels.length > 0) {
    const encoded = state.panels
      .slice(-MAX_PANELS)
      .map((panel) => `${encodeURIComponent(panel.view)}:${encodeURIComponent(panel.id)}`)
      .join(',');
    parts.push(`p=${encoded}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Кладёт панель наверх стопки. Та же панель уже открыта — поднимается наверх, а не двоится.
 * Стопка полна — нижняя вытесняется: человек идёт вглубь, и путь назад к вытесненной
 * лежит через список, из которого он её открывал.
 */
export function pushPanel(state: SearchState, panel: PanelRef): SearchState {
  const others = state.panels.filter(
    (existing) => existing.view !== panel.view || existing.id !== panel.id,
  );
  const panels = [...others, panel].slice(-MAX_PANELS);
  return { ...state, panels };
}

/** Снимает верхнюю панель (Esc). На пустой стопке ничего не делает. */
export function popPanel(state: SearchState): SearchState {
  if (state.panels.length === 0) {
    return state;
  }
  return { ...state, panels: state.panels.slice(0, -1) };
}
